import { Image } from "@/components/ui/image";
import { Check } from "@/components/icons";
import { formatConditionLabel } from "@shared/products/product-name";
import { formatCents } from "@/lib/formatting/currency";
import type { BuilderSummary } from "@/lib/listings/builder-state";
import { SelectableCard } from "@/components/ui/selectable-card";

export interface GradePricing {
  condition: string;
  medianPriceCents: number;
  minPriceCents: number;
  maxPriceCents: number;
}

type Grade = BuilderSummary["grades"][number];

interface Band {
  floor: number;
  ceiling: number;
}

interface GradeRowProps {
  productTypeSlug: string;
  grades: Grade[];
  pricing: GradePricing[];
  selected: string[];
  onToggle: (condition: string) => void;
}

function GradeThumb({ grade, isSelected }: { grade: Grade; isSelected: boolean }) {
  return (
    <span className="relative block size-16 overflow-hidden rounded-md bg-surface-2">
      {grade.imageUrl ? (
        <Image src={grade.imageUrl} alt={grade.title ?? ""} fill sizes="64px" className="object-cover" />
      ) : null}
      {isSelected ? (
        <span className="absolute left-1 top-1 grid size-4 place-items-center rounded-full bg-action-fill text-action-fill-foreground">
          <Check className="size-3" aria-hidden />
        </span>
      ) : (
        <span className="absolute bottom-1 left-1 rounded bg-surface/95 px-1 tabular-nums text-micro font-semibold text-foreground">
          {grade.count}
        </span>
      )}
    </span>
  );
}

function withSelected(grades: Grade[], selected: string[]): Grade[] {
  const missing = selected
    .filter((condition) => !grades.some((grade) => grade.condition === condition))
    .map((condition) => ({ condition, count: 0, imageUrl: null, title: null }));
  return [...grades, ...missing];
}

function GradeBand({ price, band }: { price: GradePricing | undefined; band: Band }) {
  if (!price || band.ceiling <= band.floor) {
    return <span className="mt-1.5 block h-1.5 rounded bg-surface-2" />;
  }
  const span = band.ceiling - band.floor;
  const pct = (cents: number) => `${((cents - band.floor) / span) * 100}%`;
  return (
    <span className="relative mt-1.5 block h-1.5 rounded bg-surface-2">
      <span className="sr-only">
        Range {formatCents(price.minPriceCents)} to {formatCents(price.maxPriceCents)}
      </span>
      <span
        aria-hidden
        className="absolute inset-y-0 rounded bg-action-fill opacity-70"
        style={{ left: pct(price.minPriceCents), right: `${100 - ((price.maxPriceCents - band.floor) / span) * 100}%` }}
      />
      <span aria-hidden className="absolute -top-px size-3 -translate-x-1/2 rounded-full bg-action-strong" style={{ left: pct(price.medianPriceCents) }} />
    </span>
  );
}

function GradeTile({
  grade,
  label,
  price,
  band,
  isSelected,
  onToggle,
}: {
  grade: Grade;
  label: string;
  price: GradePricing | undefined;
  band: Band;
  isSelected: boolean;
  onToggle: () => void;
}) {
  return (
    <SelectableCard
      layout="row"
      size="compact"
      isSelected={isSelected}
      aria-pressed={isSelected}
      onClick={onToggle}
      className="grid grid-cols-[auto_1fr] items-center gap-2.5 p-2"
    >
      <GradeThumb grade={grade} isSelected={isSelected} />
      <span className="min-w-0">
        <span className="block truncate text-sm font-bold text-foreground">{label}</span>
        <span className="block tabular-nums text-sm font-semibold text-foreground">
          {price ? formatCents(price.medianPriceCents) : "—"}
          <span className="ml-1 font-sans text-xs font-normal text-muted-foreground">
            {price ? "median" : "no index yet"}
          </span>
        </span>
        <GradeBand price={price} band={band} />
      </span>
    </SelectableCard>
  );
}

export function GradeRow({ productTypeSlug, grades, pricing, selected, onToggle }: GradeRowProps) {
  const priced = pricing.filter((row) => row.medianPriceCents > 0);
  const band: Band = {
    floor: Math.min(...priced.map((row) => row.minPriceCents)),
    ceiling: Math.max(...priced.map((row) => row.maxPriceCents)),
  };
  return (
    <div>
      <ul className="grid gap-2 sm:grid-cols-3 lg:grid-cols-5">
        {withSelected(grades, selected).map((grade) => (
          <li key={grade.condition}>
            <GradeTile
              grade={grade}
              label={formatConditionLabel(grade.condition, productTypeSlug) ?? grade.condition}
              price={pricing.find((row) => row.condition === grade.condition)}
              band={band}
              isSelected={selected.includes(grade.condition)}
              onToggle={() => onToggle(grade.condition)}
            />
          </li>
        ))}
      </ul>
      {priced.length > 1 ? (
        <div className="mt-1 flex justify-between px-0.5 tabular-nums text-micro text-fg-subtle">
          <span>{formatCents(band.floor)}</span>
          <span>{formatCents(band.ceiling)}</span>
        </div>
      ) : null}
    </div>
  );
}
